// src/store/sagas/apiResponseWatcherSaga.ts
import { takeEvery } from "redux-saga/effects";
import {  activateProviderSuccess } from "@/store/slices/provider.slice";

/// Constants
const TOAST_EVENT_NAME = "SHOW_GLOBAL_TOAST";

const LIFETIMES = {
  SUCCESS: 1500,
  ERROR_DEFAULT: 4000,
  ERROR_WITH_DETAILS: 8000,
  WARNING_WITH_DETAILS: 10000,
};

const ACTION_SUFFIXES = {
  SUCCESS: ["Success", "/fulfilled"],
  FAILURE: ["Failure", "/rejected"],
};

const WRITE_KEYWORDS = [
  "create",
  "add",
  "update",
  "edit",
  "patch",
  "delete",
  "remove",
  "post",
  "save",
  "activate",
];

const DEFAULT_ERROR_VALUES = {
  STATUS: "Error",
  CODE: "ERR",
};

/// //////////////////////////////////
/// Helpers
/// //////////////////////////////////

const isWriteOperation = (actionType: string) => {
  const type = actionType.toLowerCase();
  return WRITE_KEYWORDS.some((keyword) => type.includes(keyword));
};

const hasSuffix = (type: string, suffixes: string[]) =>
  suffixes.some((suffix) => type.endsWith(suffix));

/// //////////////////////////////////
/// Saga Implementation
/// //////////////////////////////////

function* handleGlobalNotifications(action: any) {
  const { type, payload } = action;

  const isSuccess = hasSuffix(type, ACTION_SUFFIXES.SUCCESS);
  const isFailure = hasSuffix(type, ACTION_SUFFIXES.FAILURE);

  // 1. Handle Success
  if (isSuccess && isWriteOperation(type)) {
    const isProviderActivation = type === activateProviderSuccess.type;
    if (payload?.isWarning && isProviderActivation) {
      window.dispatchEvent(
        new CustomEvent(TOAST_EVENT_NAME, {
          detail: {
            type: "Warning",
            message: payload?.message || "Operation successful with warnings",
            lifetime: LIFETIMES.WARNING_WITH_DETAILS,
            autoClose: true,
            payload: payload?.syncDetails ? payload : undefined,
          },
        }),
      );
    } else {
      window.dispatchEvent(
        new CustomEvent(TOAST_EVENT_NAME, {
          detail: {
            type: "Success",
            message: payload?.message || "Operation successful",
            lifetime: LIFETIMES.SUCCESS,
          },
        }),
      );
    }
  }

  // 2. Handle Failure
  if (isFailure) {
    const detectedStatus = payload?.statusCode || payload?.status || DEFAULT_ERROR_VALUES.STATUS;
    const errorMessage = payload?.message || "An unexpected error occurred";
    
    const errorBody = {
      message: errorMessage,
      statusCode: detectedStatus,
      messageDetails: payload?.messageDetails || [],
      errorCode: payload?.errorCode || DEFAULT_ERROR_VALUES.CODE,
      ...payload,
    };

    const lifetime =
      errorBody.messageDetails.length > 0
        ? LIFETIMES.ERROR_WITH_DETAILS
        : LIFETIMES.ERROR_DEFAULT;

    window.dispatchEvent(
      new CustomEvent(TOAST_EVENT_NAME, {
        detail: {
          type: "Error",
          payload: errorBody,
          lifetime,
        },
      })
    );
  }
}

export function* watchGlobalNotifications() {
  yield takeEvery("*", handleGlobalNotifications);
}